import React from "react";
import { motion } from "framer-motion";
import PicImg from "../PicImg";

/**
 * MastSectionDetails — generic blueprint/engineering-drawing showcase.
 *
 * Reusable for any product that needs to present:
 *  - Structural mast details
 *  - Dimensional drawings
 *  - Blueprint illustrations
 *  - Engineering schematics
 *  - Cross-sectional views
 *
 * Layout: matte-black section, two-column split.
 *  LEFT  — Blueprint/image inside a rounded-3xl dark frame with grid overlay,
 *          optional orange bottom caption bar.
 *  RIGHT — Eyebrow + heading + subtitle + spec list (2-col row grid).
 *
 * Props:
 *  - testId: string
 *  - eyebrow: string
 *  - heading: ReactNode
 *  - subtitle?: string
 *  - blueprint: ReactNode          (SVG element OR <PicImg loading="lazy" decoding="async" />, callers control positioning/opacity)
 *  - blueprintCaption?: {
 *      eyebrow?: string,           (default "Detail")
 *      text: string,
 *    }
 *  - blueprintFrameAspect?: string  (default "aspect-square lg:aspect-[4/5]")
 *  - specs: { parameter: string, value: string }[]
 *  - notes?: string                  (italic disclaimer / footnote below the spec list, optional)
 */
export default function MastSectionDetails({
  testId,
  eyebrow,
  heading,
  subtitle,
  blueprint,
  blueprintCaption,
  blueprintFrameAspect = "aspect-square lg:aspect-[4/5]",
  specs = [],
  notes,
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-black py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-1/2 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.12] -translate-y-1/2"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center">
        {/* LEFT — Blueprint frame */}
        <motion.div
          initial={{ opacity: 0, x: -22 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className={`relative rounded-3xl overflow-hidden bg-liftile-matte-black border border-white/[0.08] ${blueprintFrameAspect}`}
        >
          <div
            aria-hidden
            className="absolute inset-0 opacity-[0.10] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />

          {blueprint}

          {blueprintCaption && (
            <div className="absolute bottom-6 left-6 right-6">
              {blueprintCaption.eyebrow && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-8 h-px bg-liftile-orange" />
                  <span className="font-heading text-[10px] uppercase tracking-[0.28em] text-liftile-orange font-semibold">
                    {blueprintCaption.eyebrow}
                  </span>
                </div>
              )}
              <p className="font-heading text-sm md:text-base text-liftile-cream font-medium leading-snug">
                {blueprintCaption.text}
              </p>
            </div>
          )}
        </motion.div>

        {/* RIGHT — Content + Spec Table */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.7 }}
        >
          {eyebrow && <div className="heading-eyebrow mb-3">{eyebrow}</div>}
          <h2 className="display-2 text-liftile-cream">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-cream/70 mt-6 leading-relaxed">
              {subtitle}
            </p>
          )}

          {specs.length > 0 && (
            <dl
              data-testid={`${testId}-list`}
              className="mt-10 rounded-2xl bg-white/[0.03] border border-white/[0.08] overflow-hidden"
            >
              {specs.map((row, ri) => (
                <div
                  key={row.parameter}
                  className={`grid grid-cols-[1fr_1.15fr] gap-4 px-6 md:px-7 py-4 border-b border-white/[0.06] last:border-0 ${
                    ri % 2 === 0 ? "bg-white/[0.015]" : "bg-transparent"
                  }`}
                >
                  <dt className="font-heading text-[11px] uppercase tracking-[0.18em] text-liftile-orange font-semibold self-center">
                    {row.parameter}
                  </dt>
                  <dd className="font-body text-sm md:text-[15px] text-liftile-cream/85 self-center">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {notes && (
            <p className="font-body italic text-xs md:text-sm text-liftile-cream/50 mt-6">
              {notes}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
